import type { CapacitySummary } from './rows';
import { formatLinkSpeed } from './format';

/**
 * Interface types that have no line to be a rate of.
 *
 * A loopback is `up` and publishes a speed — the router in production reports
 * `lo` at 10 Mbit/s — so the series resolves it as a perfectly good
 * denominator. It is not one: a percentage of a loopback's notional rate is a
 * category error, and offering it invites a reader to act on it. The interface
 * type is the only place that distinction survives, so this is a rendering
 * decision and it belongs here rather than in the series.
 *
 * Deliberately short. Bridges are typed `ethernetCsmacd` and cannot be told
 * apart from a physical port by type alone — those fall to the series, which
 * reports them as `no_physical_rate` because they publish no speed.
 */
const NO_LINE_RATE_TYPES = new Set(['softwareLoopback']);

export function hasNoLineRate(interfaceType: string | null | undefined): boolean {
  return interfaceType !== null && interfaceType !== undefined && NO_LINE_RATE_TYPES.has(interfaceType);
}

export interface DenominatorNote {
  /** Whether a percentage of line rate can be offered at all. */
  available: boolean;
  /** One quiet sentence naming the denominator, or why there is none. */
  caption: string;
  /** Why the toggle is off, for its `title` — never hidden, always explained. */
  reason: string | null;
}

/**
 * What to say about the denominator, from the four states the wire carries.
 *
 * The states are not interchangeable and neither are the sentences. A port that
 * is down is an *answer*; a bridge makes the question a category error; and
 * only the last is a gap. Collapsing them to "unknown" is what previously
 * painted half the fleet a confident healthy green.
 */
export function denominatorNote(
  summary: CapacitySummary,
  interfaceType: string | null | undefined,
): DenominatorNote {
  if (hasNoLineRate(interfaceType)) {
    return {
      available: false,
      caption: 'A loopback has no line to be a rate of, so no percentage is offered.',
      reason: 'a loopback has no line rate',
    };
  }

  if (summary.anyKnown && summary.bits !== null) {
    const caption = `Against a ${formatLinkSpeed(summary.bits)} link.`;
    return {
      available: true,
      caption:
        summary.dominant === 'known'
          ? caption
          : `${caption} The link speed is not known for the whole window.`,
      reason: null,
    };
  }

  switch (summary.dominant) {
    case 'link_down':
      return {
        available: false,
        caption: 'This port was not up in this window, so utilization is not a question that applies.',
        reason: 'the port was not up',
      };
    case 'no_physical_rate':
      return {
        available: false,
        caption:
          'This port has no physical line rate — a bridge, a loopback or an internal fabric port — so there is no percentage to compute.',
        reason: 'this port has no physical line rate',
      };
    default:
      // The documented "a gateway older than the one that started publishing
      // link speed" case. For the first day after that rollout it is the
      // majority state on every device, so it reads as "not yet" rather than as
      // something to go and investigate.
      return {
        available: false,
        caption: 'No link-speed readings for this period yet, so no percentage can be computed.',
        reason: 'no link-speed readings for this period yet',
      };
  }
}
